<template>
    <section class="leaf board" :class="{ presenting }" aria-label="Sitzplan" @keydown.esc="movingFrom = null">
      <div class="toolbar no-print">
        <div class="actions">
          <button type="button" class="btn btn-primary" :disabled="!canGenerate" @click="generateOrder()">
            <Dices aria-hidden="true" /> Zufällige Ordnung
          </button>
          <template v-if="hasFixedPlan">
            <button type="button" class="btn" :disabled="fixedActive" :title="fixedActive ? 'Die feste Ordnung ist schon gesetzt' : 'Setzt alle auf ihren Platz in der gespeicherten festen Ordnung'" @click="applyFixedPlan">
              <BookmarkCheck aria-hidden="true" /> Feste Ordnung
            </button>
            <button v-if="!presenting" type="button" class="btn btn-icon" aria-label="Feste Ordnung bearbeiten" title="Feste Ordnung bearbeiten" @click="$emit('openFixedEditor')">
              <Pencil aria-hidden="true" />
            </button>
          </template>
          <button v-else-if="!presenting" type="button" class="btn" :disabled="!hasStudents || tableCount === 0" @click="$emit('openFixedEditor')">
            <BookmarkPlus aria-hidden="true" /> Feste Ordnung erstellen
          </button>
          <button type="button" class="btn" :disabled="!hasStudents || picking" @click="startSelection">
            <Hand aria-hidden="true" /> Zufällig auswählen
          </button>
          <button type="button" class="btn" :disabled="!hasStudents" @click="openGroupBuilder">
            <Users aria-hidden="true" /> Gruppenarbeit
          </button>
        </div>
        <div class="actions">
          <button type="button" class="btn btn-quiet btn-icon" :disabled="!canUndo" aria-label="Rückgängig" title="Letzte Änderung am Sitzplan rückgängig machen" @click="undo">
            <Undo2 aria-hidden="true" />
          </button>
          <button type="button" class="btn btn-quiet" :aria-pressed="isMirrored ? 'true' : 'false'"
            title="Dreht die Ansicht um 180°: einmal von vorne, einmal von hinten gesehen"
            @click="isMirrored = !isMirrored">
            <FlipVertical2 aria-hidden="true" /> Zimmer spiegeln
          </button>
          <template v-if="!presenting">
            <button type="button" class="btn btn-quiet" :disabled="seatedCount === 0" @click="clearPlan">
              <Eraser aria-hidden="true" /> Plätze leeren
            </button>
            <button type="button" class="btn btn-quiet btn-icon" aria-label="Drucken" title="Sitzplan drucken" @click="print">
              <Printer aria-hidden="true" />
            </button>
          </template>
          <button type="button" class="btn" @click="$emit('togglePresenting')">
            <component :is="presenting ? 'Minimize2' : 'Presentation'" aria-hidden="true" /> {{ presenting ? 'Beenden' : 'Präsentieren' }}
          </button>
        </div>
      </div>

      <!-- Always one line here, so the tables never move under the pointer -->
      <div class="status no-print" role="status">
        <p v-if="movingFrom !== null" class="form-note">
          <span><strong>{{ studentNames[seats[movingFrom]] }}</strong> ist ausgewählt. Klicke auf den neuen Platz, oder nochmals auf den Namen, um abzubrechen.</span>
          <button type="button" class="btn btn-sm pin-button" @click="togglePin">
            <component :is="movingIsPinned ? 'PinOff' : 'Pin'" aria-hidden="true" /> {{ movingIsPinned ? 'Platz lösen' : 'Platz fixieren' }}
          </button>
        </p>
        <p v-else-if="picking" class="form-note">Der Zufall entscheidet …</p>
        <p v-else-if="pickedStudent" class="form-note picked-note">
          <span>Dran ist <strong>{{ pickedStudent.name }}</strong></span>
          <span class="picked-count">{{ alreadyPicked.length }} von {{ selectedClass.students.length }} waren dran</span>
          <button type="button" class="btn btn-quiet btn-sm btn-icon" aria-label="Auswahl ausblenden" @click="resetSelection">
            <X aria-hidden="true" />
          </button>
        </p>
        <p v-else-if="notice" :class="notice.kind === 'error' ? 'form-error' : 'form-note'">{{ notice.text }}</p>
        <p v-else class="status-hint">{{ hint }}</p>
      </div>

      <RoomPlan :config="config" :mirrored="isMirrored" v-slot="{ table }">
        <Table
          :studentName="studentNames[seats[table.index]]"
          :row="Math.floor(table.index / config.cols) + 1" :col="table.index % config.cols + 1"
          :available="table.available"
          :picked="!!seats[table.index] && seats[table.index] === currentlySelectedStudent"
          :moving="table.index === movingFrom"
          :pinned="!!seats[table.index] && pinned.includes(seats[table.index])"
          v-on:toggle="clickTable(table)">
        </Table>
      </RoomPlan>

      <div class="legend no-print" v-if="!presenting">
        <ul class="key">
          <li><span class="sample sample-table"></span> Tisch</li>
          <li><span class="sample"></span> kein Tisch</li>
          <li v-if="pinned.length > 0"><Pin class="sample-pin" aria-hidden="true" /> fixierter Platz</li>
        </ul>
        <div class="actions">
          <select class="select front-select" aria-label="Wo ist die Tafel?" :value="config.front" @change="$emit('changeFront', $event.target.value)">
            <option v-for="front in fronts" :key="front.key" :value="front.key">{{ front.label }}</option>
          </select>
          <div class="stepper" role="group" aria-label="Reihen">
            <button type="button" aria-label="Eine Reihe weniger" :disabled="config.rows <= minSize" @click="changeRows(-1)"><Minus /></button>
            <output>{{ rowsLabel }}</output>
            <button type="button" aria-label="Eine Reihe mehr" :disabled="config.rows >= maxSize" @click="changeRows(1)"><Plus /></button>
          </div>
          <div class="stepper" role="group" aria-label="Spalten">
            <button type="button" aria-label="Eine Spalte weniger" :disabled="config.cols <= minSize" @click="changeCols(-1)"><Minus /></button>
            <output>{{ colsLabel }}</output>
            <button type="button" aria-label="Eine Spalte mehr" :disabled="config.cols >= maxSize" @click="changeCols(1)"><Plus /></button>
          </div>
        </div>
      </div>
    </section>
  </template>

  <script>
  import { BookmarkCheck, BookmarkPlus, Dices, Eraser, FlipVertical2, Hand, Minimize2, Minus, Pencil, Pin, PinOff, Plus, Presentation, Printer, Undo2, Users, X } from '@lucide/vue';
  import RoomPlan from './RoomPlan.vue';
  import Table from './table.vue';
  import { MAX_ROOM_SIZE, MIN_ROOM_SIZE, ROOM_FRONTS, plural } from '../config.js';

  export default {
    name: 'Room',
    data() {
      return  {
        currentlySelectedStudent: null,
        pickedStudent: null,
        alreadyPicked: [],
        picking: false,
        selectionTimer: null,
        isMirrored: false,
        movingFrom: null,
        minSize: MIN_ROOM_SIZE,
        maxSize: MAX_ROOM_SIZE,
        fronts: ROOM_FRONTS,
      }
    },
    props: {
      config: Object,
      selectedClass: Object,
      // { tableIndex: studentId } for this class in this room
      seats: Object,
      // The students who keep their seat when the plan is shuffled
      pinned: Array,
      notice: Object,
      canUndo: Boolean,
      // A fixed arrangement is saved for this class and room
      hasFixedPlan: Boolean,
      // The plan on show is that fixed arrangement
      fixedActive: Boolean,
      // Shown on the projector: no chrome, and the room can't be changed by accident
      presenting: Boolean,
    },
    emits: ['toggleTable', 'changeRows', 'changeCols', 'changeFront', 'generateOrder', 'moveSeat', 'togglePin', 'clearPlan', 'undo', 'togglePresenting', 'openGroupBuilder', 'applyFixedPlan', 'openFixedEditor'],
    components: {
      RoomPlan, Table, BookmarkCheck, BookmarkPlus, Dices, Eraser, FlipVertical2, Hand, Minimize2, Minus, Pencil, Pin, PinOff, Plus, Presentation, Printer, Undo2, Users, X,
    },
    watch: {
        'config.id': function() {
            this.resetSelection()
            this.movingFrom = null
            this.isMirrored = false
        },
        'selectedClass.id': function() {
          this.resetSelection()
          this.alreadyPicked = []
          this.movingFrom = null
        },
        seats: function() {
          if (this.movingFrom !== null && !this.seats[this.movingFrom]) this.movingFrom = null
        }
    },
    computed: {
      movingIsPinned() {
        return this.movingFrom !== null && this.pinned.includes(this.seats[this.movingFrom])
      },
      hint() {
        if (this.seatedCount > 0) return 'Klicke auf einen Namen, um den Platz zu tauschen oder zu fixieren.'
        return this.presenting ? 'Noch niemand hat einen Platz.' : 'Klicke auf ein Feld, um dort einen Tisch zu setzen oder zu entfernen.'
      },
      studentNames() {
        const names = {}
        this.selectedClass.students.forEach(s => { names[s.id] = s.name })
        return names
      },
      tableCount() {
        return this.config.tables.filter(t => t.available).length
      },
      seatedCount() {
        return Object.keys(this.seats).length
      },
      hasStudents() {
        return this.selectedClass.students.length > 0
      },
      canGenerate() {
        return this.hasStudents && this.selectedClass.students.length <= this.tableCount
      },
      rowsLabel() {
        return plural(this.config.rows, 'Reihe', 'Reihen')
      },
      colsLabel() {
        return plural(this.config.cols, 'Spalte', 'Spalten')
      },
    },
    beforeUnmount: function() {
      clearTimeout(this.selectionTimer)
    },
    methods: {
      changeRows: function(amount) {
        this.$emit('changeRows', amount);
      },
      changeCols: function(amount) {
        this.$emit('changeCols', amount);
      },
        // Runs over a few names, then lands on someone who hasn't had a turn yet.
        startSelection: function() {
          const students = this.selectedClass.students
          if (this.picking || students.length === 0) return
          let candidates = students.filter(s => !this.alreadyPicked.includes(s.id))
          if (candidates.length === 0) {
            this.alreadyPicked = []
            candidates = students
          }
          const chosen = this.choose(candidates)
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          let steps = reducedMotion ? 0 : 14
          this.movingFrom = null
          this.pickedStudent = null
          this.picking = true
          const step = () => {
            if (steps === 0) {
              this.currentlySelectedStudent = chosen.id
              this.pickedStudent = chosen
              this.alreadyPicked.push(chosen.id)
              this.picking = false
              return
            }
            steps -= 1
            this.currentlySelectedStudent = this.choose(students).id
            this.selectionTimer = setTimeout(step, 130)
          }
          step()
        },
        resetSelection: function() {
          clearTimeout(this.selectionTimer)
          this.picking = false
          this.pickedStudent = null
          this.currentlySelectedStudent = null
        },
        choose: function (choices) {
          var index = Math.floor(Math.random() * choices.length);
          return choices[index];
        },
        // A seated student is picked up and put down; any other field is a table switched on or off.
        clickTable: function(table) {
          if (this.movingFrom !== null) {
            if (table.available && table.index !== this.movingFrom) {
              this.$emit('moveSeat', { from: this.movingFrom, to: table.index })
            }
            this.movingFrom = null
            return
          }
          if (this.seats[table.index]) {
            this.movingFrom = table.index
            return
          }
          if (this.presenting) return
          this.$emit('toggleTable', table.index);
        },
        togglePin: function() {
          this.$emit('togglePin', this.seats[this.movingFrom])
          this.movingFrom = null
        },
        undo: function() {
          this.resetSelection()
          this.movingFrom = null
          this.$emit('undo')
        },
        generateOrder: function() {
            this.resetSelection()
            this.movingFrom = null
            this.$emit("generateOrder");
        },
        applyFixedPlan: function() {
          this.resetSelection()
          this.movingFrom = null
          this.$emit('applyFixedPlan')
        },
        clearPlan: function() {
          this.resetSelection()
          this.movingFrom = null
          this.$emit('clearPlan')
        },
        openGroupBuilder: function() {
          this.$emit("openGroupBuilder")
        },
        print: function() {
          window.print()
        }
    }
  }
  </script>

  <style scoped>
  .board {
    display: grid;
    gap: var(--s-4);
    padding: var(--s-5);
    min-width: 0;
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--s-3);
  }

  .status p {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--s-1) var(--s-4);
    min-height: 48px;
    padding-top: var(--s-1);
    padding-bottom: var(--s-1);
  }

  .status strong {
    font-weight: 800;
  }

  .status-hint {
    font-size: var(--t-sm);
    font-weight: 600;
    color: var(--ink-2);
  }

  .picked-note {
    font-size: var(--t-lg);
  }

  .pin-button {
    margin-left: auto;
  }

  .picked-count {
    margin-left: auto;
    font-size: var(--t-xs);
    color: var(--ink-2);
  }

  /* On the projector the room takes all the height there is; fields without a table step back. */
  .board.presenting {
    grid-template-rows: auto auto minmax(0, 1fr);
    min-height: 0;
  }

  .presenting .grid-frame {
    display: flex;
    min-height: 0;
  }

  .presenting :deep(.room-plan) {
    flex: 1;
    min-height: 0;
  }

  .presenting :deep(.grid) {
    flex: 1 1 0;
    align-self: stretch;
    height: auto;
    min-height: calc(var(--rows) * 30px);
  }

  .presenting .cell:not(.available) {
    visibility: hidden;
  }

  .presenting :deep(.cell-name) {
    font-size: clamp(0.6875rem, min(14cqw, 40cqh), 2.5rem);
  }

  .presenting :deep(.cell-pin) {
    display: none;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--s-3) var(--s-5);
    margin: 0 calc(-1 * var(--s-5)) calc(-1 * var(--s-5));
    padding: var(--s-3) var(--s-5);
    border-top: var(--line);
    border-radius: 0 0 12px 12px;
    background: var(--hue-wash, var(--ground-wash));
  }

  .key {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--s-2) var(--s-4);
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: var(--t-xs);
    font-weight: 700;
  }

  .key li {
    display: inline-flex;
    align-items: center;
    gap: var(--s-2);
  }

  .sample {
    flex: none;
    width: 26px;
    height: 18px;
    border: 1px solid var(--hairline);
    border-radius: 5px;
  }

  .sample-table {
    border: var(--line);
    background: var(--paper);
  }

  .sample-pin {
    width: 14px;
    height: 14px;
    stroke-width: 3;
  }

  .front-select {
    width: auto;
  }

  @media (max-width: 599px) {
    .board {
      padding: var(--s-4);
    }

    .legend {
      margin: 0 calc(-1 * var(--s-4)) calc(-1 * var(--s-4));
      padding: var(--s-3) var(--s-4);
    }

  }

  @media print {
    .board {
      padding: 0;
      border: 0;
    }
  }
  </style>
